import { IAttribute } from '../components/DTStudio/interfaces'
import { AttributeType } from '../constants/constants'

export const buildAttributeKey = (
    attributeId: string,
    entityTemplateId: string,
): string => `${attributeId} (${entityTemplateId})`

/**
 * Extracts the entity template id from a key produced by buildAttributeKey
 * (`${attributeId} (${entityTemplateId})`) — the inverse of buildAttributeKey. Returns an
 * empty string if the key is not in the expected format.
 */
export const parseTemplateIdFromKey = (key: string): string => {
    const match = key.match(/\(([^)]*)\)\s*$/)
    return match ? match[1] : ''
}

/**
 * Returns the attributes that are BOTH pre-selected in the application settings (their
 * `${attributeId} (${entityTemplateId})` key is present in preSelectedAttributeKeys) AND
 * chartable-and-not-hidden. `entityTemplateId` must be the template the settings keyed the
 * attributes against — for a relation that is the relation's child-entity template id. This
 * can differ from a related instance's own templateId: a relation is configured against a
 * parent template (e.g. "Device"), but the related instances may be of its sub-templates, so
 * an instance's templateId is a sub-template id rather than the parent the settings used.
 * (UBQ-5476)
 */
export const getPreSelectedChartableAttributes = (
    attributes: IAttribute[],
    entityTemplateId: string,
    preSelectedAttributeKeys: string[],
): IAttribute[] =>
    attributes.filter(
        (attribute) =>
            preSelectedAttributeKeys.includes(
                buildAttributeKey(attribute.id, entityTemplateId),
            ) && isChartableAndNotHidden(attribute),
    )

export const getAttributeById = (
    attributes: IAttribute[] | undefined,
    id: string,
): IAttribute | undefined => {
    return attributes?.find((attribute) => attribute.id === id)
}

export const getAttributeByName = (
    attributes: IAttribute[],
    name: string,
): IAttribute | undefined => {
    return attributes.find((attribute) => attribute.name === name)
}

export const isChartableOrMappableOrTimeSeriesAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!(
        (item?.metadata?.TimeSeries ||
            item?.metadata?.Chartable ||
            item?.metadata?.Mappable) &&
        !item?.metadata?.Hidden
    )
}

export const isChartableAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!item?.metadata?.Chartable && !item?.metadata?.Hidden
}

export const isChartable = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Chartable
}

export const isMappable = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Mappable && (isGeoFence(item) || isGPS(item))
}

export const isMappableTimeSeriesAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!(
        item?.metadata?.Mappable &&
        item?.metadata?.TimeSeries &&
        !item?.metadata?.Hidden
    )
}

/**
 *
 * @param item attribute
 * @returns This method checks if an attribute of type Entity is mappable or not
 */
export const isMappableEntityAttribute = (
    item: IAttribute | undefined,
): boolean => {
    return !!item?.metadata?.Mappable && isEntity(item)
}

export const isDynamic = (item: IAttribute | undefined): boolean => {
    return item?.attributeType === 'Dynamic'
}

export const isTimeSeries = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.TimeSeries
}

export const isGeoFence = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GEO_FENCING
}

export const isGPS = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GPS
}

export const isDate = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE
}

export const isDateTime = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE_TIME
}

export const isURL = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.URL
}

export const isEntity = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.ENTITY
}

export const isJson = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.JSON
}
export const isRadio = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.RADIO
}

export const isStringType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.STRING
}

export const isNumberType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.NUMBER
}

export const isDecimalType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DECIMAL
}

export const isSelect = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.SELECT
}

export const isMultiSelect = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.MULTI_SELECT
}

export const isUser = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER
}

export const isUserGroup = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER_GROUP
}

export const isHidden = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Hidden
}

export const isNotHidden = (item: IAttribute | undefined): boolean => {
    return !isHidden(item)
}

export const isPDFBase64 = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.PDFBASE64
}
export const isEmail = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.EMAIL
}

export const isCurrency = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.CURRENCY
}

export const isTemperature = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.TEMPERATURE
}

export const isRelativeHumidity = (item: IAttribute | undefined): boolean => {
    return (
        item?.dataType?.toLocaleLowerCase() === AttributeType.RELATIVE_HUMIDITY
    )
}

export const isWeight = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.WEIGHT
}

export const isDistance = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DISTANCE
}

export const isFile = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.FILE
}

export const isBoolean = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.BOOLEAN
}

export const isEntityJson = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.Entity_JSON
}

export const isHumidity = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.HUMIDITY
}

export const isCustomAttribute = (item: IAttribute | undefined): boolean => {
    return (
        item?.dataType?.toLocaleLowerCase() === AttributeType.CUSTOM_ATTRIBUTE
    )
}

export const isCountryISOcode = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.COUNTRYISOCODE
}

export const isAlaramSatus = (item: IAttribute | undefined): boolean => {
    return item?.id === 'alarmStatus'
}

export const isEmptyValue = (
    value: string | unknown[] | Record<string, unknown> | null | undefined,
): boolean => {
    if (value == null) return true
    if (typeof value === 'string') return value.trim().length === 0
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object' && !Array.isArray(value))
        return Object.keys(value).length === 0
    return false
}

export const isSeparator = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.SEPARATOR
}

const GENERATED_SEPARATOR_NAME = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i

/**
 * UBQ-5642: a separator's description is its display name, so that an org can override it
 * through the same per-org mechanism as every other attribute name. Separators are created
 * with a generated uuid as their display name, so a uuid means "no description was ever set"
 * and the separator stays a bare line — returning '' keeps those unchanged.
 */
/**
 * UBQ-5642: formatting tags a separator description may use. Deliberately tiny, and NEVER with
 * attributes - that is what keeps a value coming from the database out of script, link and event
 * handler territory, without pulling in a sanitiser dependency.
 */
const SEPARATOR_DESCRIPTION_TAGS = ['b', 'strong', 'i', 'em', 'small', 'br']

/**
 * Escapes a separator description completely, then puts back only the tags listed above. Anything
 * else - a script tag, an attribute, an unknown tag - survives as visible text rather than markup,
 * so a description can never do more than embolden or shrink a few words.
 */
export const toSeparatorDescriptionHtml = (description: string): string => {
    const escaped = description
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    const tags = SEPARATOR_DESCRIPTION_TAGS.join('|')
    return escaped.replace(
        new RegExp(`&lt;(/?)(${tags})\\s*/?&gt;`, 'gi'),
        (_match, slash: string, tag: string) =>
            `<${slash}${tag.toLowerCase()}>`,
    )
}

export const getSeparatorDescription = (name: string | undefined): string => {
    const description = name?.trim() ?? ''
    return GENERATED_SEPARATOR_NAME.test(description) ? '' : description
}
